// @ts-nocheck
import { Fragment, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { ChevronDown, ChevronRight, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import RegistrationDetailPanel from './RegistrationDetailPanel';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

interface Props {
  team: any;
  onViewRoster?: (registrationId: string) => void;
}

const statusVariant: Record<string, string> = {
  draft: 'bg-muted text-muted-foreground',
  registered: 'bg-blue-100 text-blue-800',
  pending_payment: 'bg-blue-100 text-blue-800',
  checked_in: 'bg-purple-100 text-purple-800',
  completed: 'bg-green-100 text-green-800',
  waitlisted: 'bg-amber-100 text-amber-800',
  dropped: 'bg-red-100 text-red-800',
  refunded: 'bg-red-100 text-red-800',
};

const errorMessages: Record<string, string> = {
  NO_BALANCE: 'One of the selected registrations is already paid. Please deselect it and try again.',
  UNAUTHORIZED_ROLE: 'You do not have permission to pay for these registrations.',
  FORBIDDEN: 'You do not have billing access for this team.',
  STRIPE_ERROR: 'There was a problem starting checkout. Please try again.',
};

export default function TeamRegistrationsList({ team, onViewRoster }: Props) {
  const queryClient = useQueryClient();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [paying, setPaying] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['team-registrations', team.id],
    queryFn: async () => {
      const { data, error } = await invokeEdgeFunction('rle-list-team-registrations', {
        body: { team_id: team.id },
      });
      if (error) throw error;
      return data;
    },
  });

  const registrations = data?.data?.registrations ?? [];

  const selectedRows = registrations.filter((r: any) => selected[r.id] && (r.balance_due ?? 0) > 0);
  const selectedCount = selectedRows.length;
  const selectedTotal = selectedRows.reduce((sum: number, r: any) => sum + (r.balance_due ?? 0), 0);

  const toggleSelected = (id: string, checked: boolean) => {
    setSelected((prev) => ({ ...prev, [id]: checked }));
  };

  const buildUrls = () => {
    const base = `${window.location.origin}${window.location.pathname}`;
    return {
      success_url: `${base}?cart_session={CHECKOUT_SESSION_ID}`,
      cancel_url: `${base}?cart_canceled=1`,
    };
  };

  const startCheckout = async (items: any[]) => {
    const { success_url, cancel_url } = buildUrls();
    const { data, error } = await invokeEdgeFunction('rle-create-billing-checkout', {
      body: { items, success_url, cancel_url },
    });
    if (error) {
      const code = error.message?.trim();
      if (code === 'NO_BALANCE') {
        queryClient.invalidateQueries({ queryKey: ['team-registrations', team.id] });
      }
      toast.error(errorMessages[code] ?? error.message);
      return;
    }
    const url = data?.data?.url;
    if (!url) {
      toast.error('No checkout URL was returned. Please try again.');
      return;
    }
    window.location.href = url;
  };

  const [payingRowId, setPayingRowId] = useState<string | null>(null);

  const handlePayRow = async (regId: string) => {
    setPayingRowId(regId);
    try {
      await startCheckout([{ type: 'registration', id: regId }]);
    } catch (e: any) {
      toast.error(errorMessages[e?.message?.trim()] ?? e?.message ?? 'Failed to start checkout.');
    } finally {
      setPayingRowId(null);
    }
  };

  const handlePaySelected = async () => {
    if (selectedCount === 0) return;
    setPaying(true);
    try {
      const items = selectedRows.map((r: any) => ({ type: 'registration', id: r.id }));
      await startCheckout(items);
    } catch (e: any) {
      toast.error(errorMessages[e?.message?.trim()] ?? e?.message ?? 'Failed to start checkout.');
    } finally {
      setPaying(false);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-4">
        <CardTitle>Registrations</CardTitle>
        {selectedCount > 0 && (
          <Button onClick={handlePaySelected} disabled={paying}>
            <CreditCard className="h-4 w-4 mr-2" />
            {paying ? 'Starting checkout…' : `Pay selected (${selectedCount}) — $${selectedTotal.toFixed(2)}`}
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : registrations.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">No registrations yet.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"></TableHead>
                <TableHead className="w-8"></TableHead>
                <TableHead>Event</TableHead>
                <TableHead>Dates</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Payment</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Roster</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {registrations.map((r: any) => {
                const open = expandedId === r.id;
                const balanceDue = r.balance_due ?? 0;
                const isPaid = balanceDue <= 0;
                return (
                  <Fragment key={r.id}>
                    <TableRow
                      className="cursor-pointer"
                      onClick={() => setExpandedId(open ? null : r.id)}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={!!selected[r.id] && !isPaid}
                          disabled={isPaid}
                          onCheckedChange={(c) => toggleSelected(r.id, !!c)}
                          aria-label={`Select ${r.event?.name ?? 'registration'}`}
                        />
                      </TableCell>
                      <TableCell>
                        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </TableCell>
                      <TableCell className="font-medium">{r.event?.name}</TableCell>
                      <TableCell>{r.event?.start_date} – {r.event?.end_date}</TableCell>
                      <TableCell>
                        <span className={`inline-block px-2 py-0.5 rounded text-xs ${statusVariant[r.registration_status] ?? 'bg-muted'}`}>
                          {r.registration_status}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className={`inline-block px-2 py-0.5 rounded text-xs ${isPaid ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                          {isPaid ? 'paid' : 'balance due'}
                        </span>
                      </TableCell>
                      <TableCell>${(r.total_amount_paid ?? 0).toFixed(2)} / ${balanceDue.toFixed(2)}</TableCell>
                      <TableCell>{r.roster_size ?? '—'}</TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <div className="flex gap-2 flex-wrap">
                          {balanceDue > 0 && (
                            <Button
                              size="sm"
                              disabled={payingRowId === r.id}
                              onClick={() => handlePayRow(r.id)}
                            >
                              {payingRowId === r.id ? 'Starting…' : 'Pay now'}
                            </Button>
                          )}
                          {onViewRoster && (
                            <Button size="sm" variant="ghost" onClick={() => onViewRoster(r.id)}>
                              View roster
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => setExpandedId(open ? null : r.id)}>
                            {open ? 'Hide details' : 'View details'}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    {open && (
                      <TableRow>
                        <TableCell colSpan={9} className="p-3">
                          <RegistrationDetailPanel registrationId={r.id} teamName={team?.name} />
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
